import { IssueCaseDoc, IssueTaskDoc } from '@gooduncles/gu-app-schema';
import { AgGridReact } from 'ag-grid-react';
import { DatePicker, Tabs } from 'antd';
import dayjs from 'dayjs';
import { FC, useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { formatDate, getDefaultDates } from 'src/utils/date-util';

import { useIssueCaseHistoryData, useIssueTaskHistoryData } from 'src/hooks/subscription/useIssueHistoryData';

import AgGridSettingsButtons from 'src/atomic-components/molecules/AgGridSettingsButtons/AgGridSettingsButtons';

import { IssueCaseSummaryCard, IssueTaskSummaryCard } from './IssueHistory/IssueHistorySummary';
import IssueCaseHistoryTable, { ISSUE_CASE_HISTORY_TABLE_ID } from './IssueHistory/IssueCaseHistoryTable';
import IssueTaskHistoryTable, { ISSUE_TASK_HISTORY_TABLE_ID } from './IssueHistory/IssueTaskHistoryTable';

const { RangePicker } = DatePicker;

// 기본값: 최근 7일
const defaultDates = getDefaultDates(7, 0);

/**
 * 미래의 날짜를 선택하지 못하도록 하는 함수
 */
const disableFutureDate = (current: dayjs.Dayjs) => {
  return current && current > dayjs().endOf('day');
};

const IssueHistory: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') ?? 'case';
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>(defaultDates);

  // 탭별 gridRef 관리
  const [caseGridRef, setCaseGridRef] = useState<AgGridReact<IssueCaseDoc> | null>(null);
  const [taskGridRef, setTaskGridRef] = useState<AgGridReact<IssueTaskDoc> | null>(null);

  // 현재 활성 탭의 gridRef와 tableId
  const activeGridRef = tab === 'case' ? caseGridRef : taskGridRef;
  const activeTableId = tab === 'case' ? ISSUE_CASE_HISTORY_TABLE_ID : ISSUE_TASK_HISTORY_TABLE_ID;

  const startDate = useMemo(() => (dateRange[0] ? formatDate(dateRange[0].toDate(), 'yyyy-MM-dd') : ''), [dateRange]);
  const endDate = useMemo(() => (dateRange[1] ? formatDate(dateRange[1].toDate(), 'yyyy-MM-dd') : ''), [dateRange]);

  // 탭이 활성화될 때만 데이터 구독
  const isCaseTabActive = tab === 'case';
  const isTaskTabActive = tab === 'task';

  const {
    cases,
    summary: caseSummary,
    isLoading: isCaseLoading,
  } = useIssueCaseHistoryData({
    enabled: isCaseTabActive && !!startDate && !!endDate,
    startDate,
    endDate,
  });

  const {
    tasks,
    summary: taskSummary,
    isLoading: isTaskLoading,
  } = useIssueTaskHistoryData({
    enabled: isTaskTabActive && !!startDate && !!endDate,
    startDate,
    endDate,
  });

  const onTabChange = useCallback(
    (newTab: string) => {
      setSearchParams({ tab: newTab });
    },
    [setSearchParams]
  );

  const handleDateRangeChange = (dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) => {
    if (dates) {
      setDateRange(dates);
    }
  };

  return (
    <div className='height100 flexColumn' style={{ gap: 16 }}>
      <div className='tableButtons'>
        <AgGridSettingsButtons gridRef={activeGridRef} tableId={activeTableId} />
      </div>
      <div className='flexRow flexAlignCenter' style={{ gap: 16 }}>
        <span>조회 기간:</span>
        <RangePicker
          value={dateRange}
          onChange={handleDateRangeChange}
          disabledDate={disableFutureDate}
          allowClear={false}
        />
      </div>

      <Tabs
        type='card'
        activeKey={tab}
        onChange={onTabChange}
        style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
        tabBarStyle={{ marginBottom: 0 }}
        items={[
          {
            key: 'case',
            label: '이슈 케이스',
            children: (
              <div className='height100 flexColumn' style={{ gap: 16, paddingTop: 16 }}>
                <IssueCaseSummaryCard summary={caseSummary} isLoading={isCaseLoading} />
                <div style={{ flex: 1, minHeight: 0 }}>
                  <IssueCaseHistoryTable rowData={cases} isLoading={isCaseLoading} onGridRefChange={setCaseGridRef} />
                </div>
              </div>
            ),
          },
          {
            key: 'task',
            label: '이슈 태스크',
            children: (
              <div className='height100 flexColumn' style={{ gap: 16, paddingTop: 16 }}>
                <IssueTaskSummaryCard summary={taskSummary} isLoading={isTaskLoading} />
                <div style={{ flex: 1, minHeight: 0 }}>
                  <IssueTaskHistoryTable rowData={tasks} isLoading={isTaskLoading} onGridRefChange={setTaskGridRef} />
                </div>
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};

export default IssueHistory;
